import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { SEGMENTS, VALID_PRICES } from '../../data/conjointData';
import { formatPrice } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, LEGEND_PROPS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Props {
  selectedPrice?: number;
}

export function BimodalVisualization({ selectedPrice }: Props) {
  const isMobile = useIsMobile();
  const data = VALID_PRICES.map(price => {
    const row: Record<string, number | string> = { price: formatPrice(price), rawPrice: price };
    SEGMENTS.forEach(seg => { row[seg.shortName] = parseFloat(((seg.priceCurve[price] ?? 0) * seg.weight * 100).toFixed(2)); });
    return row;
  });

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Two Distinct Buyer Groups</h3>
        <InfoButton helpKey="bimodalStructure" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Weighted demand contribution by segment at each price — reveals two distinct market clusters</p>
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="price" {...X_AXIS_PROPS} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => `${v}%`} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number, name: string) => [`${value.toFixed(2)}%`, name]} />
          <Legend {...LEGEND_PROPS} />
          {SEGMENTS.map((seg, si) => (
            <Bar
              key={seg.shortName}
              dataKey={seg.shortName}
              stackId="demand"
              fill={seg.color}
              radius={si === SEGMENTS.length - 1 ? [4, 4, 0, 0] : undefined}
            >
              {data.map((entry, i) => {
                const isSelected = selectedPrice !== undefined && (entry.rawPrice as number) === selectedPrice;
                return (
                  <Cell
                    key={i}
                    fillOpacity={selectedPrice !== undefined ? (isSelected ? 1 : 0.35) : 0.85}
                    stroke={isSelected ? seg.color : 'none'}
                    strokeWidth={isSelected ? 2 : 0}
                  />
                );
              })}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface-alt)' }}>
          <div className="font-semibold mb-1" style={{ color: SEGMENTS[2].color }}>{SEGMENTS[2].shortName} (<span className="font-mono">{(SEGMENTS[2].percentage * 100).toFixed(0)}%</span>)</div>
          <p className="text-secondary">Very high demand at <span className="font-mono">$499</span>, cliff above ~<span className="font-mono">$500</span>. Need a low-tier product.</p>
        </div>
        <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface-alt)' }}>
          <div className="font-semibold mb-1" style={{ color: SEGMENTS[0].color }}>{SEGMENTS[0].shortName} (<span className="font-mono">{(SEGMENTS[0].percentage * 100).toFixed(1)}%</span>)</div>
          <p className="text-secondary">Moderate slope across all prices. Primary revenue opportunity.</p>
        </div>
        <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface-alt)' }}>
          <div className="font-semibold mb-1" style={{ color: SEGMENTS[1].color }}>{SEGMENTS[1].shortName} (<span className="font-mono">{(SEGMENTS[1].percentage * 100).toFixed(1)}%</span>)</div>
          <p className="text-secondary">Near-zero demand at any price. Don&apos;t waste marketing dollars.</p>
        </div>
      </div>
    </div>
  );
}
